import { useCallback, useEffect, useRef, useState } from 'react';

import { readSurveyDraft, writeSurveyDraft, type RestoredSurveyDraft } from '../state/surveyDrafts';

const DEBOUNCE_MS = 500;

interface UseSurveyDraftOptions {
  userId: string | undefined;
  /** Persistence is off while this is empty — there is no survey to key a draft to. */
  surveyGroupId: string | undefined;
  surveyDefinitionId: string;
  /**
   * Suspends saving while a submission is in flight. Set from the form's own
   * submitting state — see the pause handling below for why this is not
   * merely an optimization.
   */
  isPaused?: boolean;
}

/**
 * Persists a survey's partly-filled answers so someone who leaves mid-survey
 * comes back to what they had written rather than an empty form.
 *
 * Restoring is one-shot by design: `restoredValues` is what was on disk when
 * the survey opened, and the caller seeds the form with it once. Continuing to
 * push storage into a live form would fight the user for control of fields
 * they are typing in.
 *
 * Saving is debounced, matching the chat composer's draft handling, so a long
 * free-text answer costs one write per pause rather than one per keystroke.
 * A pending write is flushed on unmount, since leaving the screen is the case
 * this hook exists for and is exactly when the last keystrokes would be lost.
 *
 * While `isPaused` is set, any queued write is flushed once and further saves
 * are ignored until it clears. That is what keeps a submitted survey's draft
 * from coming back: the caller discards the draft once the response is
 * accepted, and without the pause a debounced write still queued from the last
 * keystroke could land after that discard and restore answers the user has
 * already sent. Saving resumes if the submission fails and the user goes back
 * to editing.
 *
 * The unmount flush below deliberately does not re-check the pause: nothing is
 * left pending by the time a paused screen unmounts, because pausing already
 * drained it. Keep that true — a change that leaves values queued through a
 * pause puts the resurrection back.
 */
export function useSurveyDraft({
  userId,
  surveyGroupId,
  surveyDefinitionId,
  isPaused = false,
}: UseSurveyDraftOptions) {
  const [restoredDraft, setRestoredDraft] = useState<RestoredSurveyDraft | null>(null);

  const [isDraftLoaded, setIsDraftLoaded] = useState(false);

  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const pendingValuesRef = useRef<Record<string, unknown> | null>(null);

  /** The page showing when a save was queued, so a flush records where they were. */
  const pageNameRef = useRef<string | undefined>(undefined);

  const isPausedRef = useRef(isPaused);

  /** Kept in a ref so the unmount flush reads the current target, not the one captured at mount. */
  const targetRef = useRef({ userId, surveyGroupId, surveyDefinitionId });

  targetRef.current = { userId, surveyGroupId, surveyDefinitionId };

  const flushValues = useCallback((values: Record<string, unknown>) => {
    const { userId, surveyGroupId, surveyDefinitionId } = targetRef.current;

    if (!surveyGroupId || !surveyDefinitionId) {
      return;
    }

    writeSurveyDraft(userId, surveyGroupId, surveyDefinitionId, values, pageNameRef.current).catch(
      () => undefined
    );
  }, []);

  const cancelPendingSave = useCallback(() => {
    if (debounceRef.current) {
      clearTimeout(debounceRef.current);

      debounceRef.current = null;
    }

    pendingValuesRef.current = null;
  }, []);

  /**
   * Writes out whatever was still queued when the submission started, then
   * stops saving until it finishes.
   *
   * Flushing rather than discarding matters because the debounce restarts on
   * every keystroke: someone who types straight through and submits without
   * pausing has nothing on disk yet, and it is exactly their failed submission
   * that the draft has to survive. The write is issued before the submission
   * is even in flight, so it lands well ahead of the discard a successful
   * response triggers, and storage preserves that order.
   */
  useEffect(() => {
    isPausedRef.current = isPaused;

    if (!isPaused) {
      return;
    }

    const pendingValues = pendingValuesRef.current;

    cancelPendingSave();

    if (pendingValues) {
      flushValues(pendingValues);
    }
  }, [isPaused, cancelPendingSave, flushValues]);

  useEffect(() => {
    let isMounted = true;

    // A write still queued belongs to the survey being navigated away from,
    // and the target is only read when it fires — left alone it would land
    // under the incoming survey, stamping one version's answers with
    // another's definition id and slipping past the mismatch check on read.
    cancelPendingSave();

    setIsDraftLoaded(false);

    setRestoredDraft(null);

    if (!surveyGroupId || !surveyDefinitionId) {
      setIsDraftLoaded(true);

      return;
    }

    const loadDraft = async () => {
      const draft = await readSurveyDraft(userId, surveyGroupId, surveyDefinitionId);

      if (!isMounted) {
        return;
      }

      setRestoredDraft(draft);

      setIsDraftLoaded(true);
    };

    Promise.resolve(loadDraft()).catch(() => undefined);

    return () => {
      isMounted = false;
    };
  }, [userId, surveyGroupId, surveyDefinitionId, cancelPendingSave]);

  const saveDraft = useCallback(
    (values: Record<string, unknown>, pageName?: string) => {
      if (isPausedRef.current) {
        return;
      }

      pendingValuesRef.current = values;

      pageNameRef.current = pageName;

      if (debounceRef.current) {
        clearTimeout(debounceRef.current);
      }

      debounceRef.current = setTimeout(() => {
        const pendingValues = pendingValuesRef.current;

        debounceRef.current = null;

        pendingValuesRef.current = null;

        if (pendingValues) {
          flushValues(pendingValues);
        }
      }, DEBOUNCE_MS);
    },
    // The write target is read from a ref at flush time, so a change to it
    // must not hand callers a new `saveDraft` and restart their subscription.
    [flushValues]
  );

  useEffect(() => {
    return () => {
      const pendingValues = pendingValuesRef.current;

      cancelPendingSave();

      if (pendingValues) {
        flushValues(pendingValues);
      }
    };
  }, [cancelPendingSave, flushValues]);

  return { restoredDraft, isDraftLoaded, saveDraft };
}
